/**
 * Settings > Case Preparation > Exhibits: the title style row
 * (`ExhibitTitlesGroup`) and the label and citation rows
 * (`ExhibitNumberingGroup`), in one card.
 *
 * - **Title style**: how the firm likes exhibits titled, in words or as a
 *   format per document type, saved as the firm's `title_instructions` (the
 *   one-line `default_title_template` can't hold it). Applies to titles
 *   proposed after it changes; titles already on files stay.
 * - **Exhibit labels** (`default_exhibit_scheme`): the scheme a new case
 *   starts with, picked from the Case Files numbering styles. Existing cases
 *   keep theirs.
 * - **Citation style** (`default_exhibit_scheme.citation_style`): how every
 *   case without its own cites exhibits in prose ("(Ex. 12, p. 3)"). It is
 *   stored on the scheme, so saving one with no label style saved also saves
 *   CaseBlink's default labels. A playbook's citation rule arrives as
 *   `citation_style_proposal`, saved here in one click.
 *
 * Both groups render only once the firm has loaded, so a save always spreads
 * the stored scheme and never drops a field it didn't read.
 */
import { useCallback, useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { LawFirmsAPI } from "@/api/lawFirms";
import {
  CASE_PREP_TEXT_MAX,
  casePrepPatch,
  casePrepValue,
  hasCasePrepField,
  type CitationStyle,
  type LawFirm,
  type TitleTemplate
} from "@/api/types/lawFirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ExhibitSchemeDialog } from "@/features/caseFiles/components/ExhibitSchemeDialog";
import {
  CUSTOM_STYLE_VALUE,
  ExhibitStyleSelectItems
} from "@/features/caseFiles/components/ExhibitStyleSelectItems";
import {
  applyPreset,
  EXHIBIT_STYLE_PRESETS,
  matchPreset,
  presetName,
  presetSamples,
  type ExhibitStylePreset
} from "@/features/caseFiles/exhibitStylePresets";
import {
  DEFAULT_EXHIBIT_SCHEME,
  type ExhibitScheme
} from "@/features/caseFiles/types";
import { styleFromTemplate } from "@/features/caseIde/workflows/titleStyle";
import { announceFirmUpdated } from "@/features/lawFirms/firmEvents";
import { cn } from "@/lib/utils";
import type { ExhibitStyle } from "@/types/exhibit_formats";

import { CasePrepRow, NeedsBackendNotice } from "./casePrepParts";

export const BUILT_IN_TEMPLATE = "{doc_type} of {subject} ({qualifier})";

const FIRST_LABEL: Record<string, string> = {
  LETTER: "A",
  NUMERIC: "1",
  ROMAN_NUMERAL: "I"
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** "Tab 1", "Exhibit A", ...: the first label a scheme gives. */
export const firstLabelOf = (
  scheme: Record<string, unknown> | null | undefined
): string | null => {
  if (!scheme) return null;
  const labeled = Array.isArray(scheme.levels)
    ? scheme.levels.find(
        (l): l is Record<string, unknown> => isRecord(l) && l.token !== "NONE"
      )
    : undefined;
  const fallback = isRecord(scheme.default_style)
    ? scheme.default_style
    : isRecord(scheme.flat)
    ? scheme.flat
    : null;
  const style = labeled ?? fallback;
  if (!style) return null;
  const token = typeof style.token === "string" ? style.token : "NUMERIC";
  const start = typeof scheme.start_at === "number" ? scheme.start_at : 1;
  let first = FIRST_LABEL[token] ?? "1";
  if (start > 1 && token === "NUMERIC") first = String(start);
  if (style.case === "LOWER") first = first.toLowerCase();
  const prefix = typeof style.prefix === "string" ? style.prefix.trim() : "";
  return [prefix, first].filter(Boolean).join(" ");
};

/** "(Ex. 12, p. 3)": one citation in `style`; the built-in is "(Exhibit 12)". */
export const citationExample = (
  style: CitationStyle | null | undefined
): string => {
  const text = `${style?.prefix?.trim() || "Exhibit"} 12`;
  return style?.print_page
    ? `(${text}${style.pincite ?? " at "}3)`
    : `(${text})`;
};

/** The citation every case without its own style uses. */
export const DEFAULT_CITATION = citationExample(null);

/** "Letters (A., B., C.)", "Numbers (1., 1.1.)", or a custom scheme's first label. */
export const labelStyleOf = (scheme: ExhibitScheme | null): string | null => {
  if (!scheme) return null;
  const preset = matchPreset(scheme);
  if (preset) return presetName(scheme, preset);
  const first = firstLabelOf(scheme as unknown as Record<string, unknown>);
  const base = first
    ? `a custom style starting at "${first}"`
    : "a custom style";
  const unlabeled = scheme.levels
    .map((style, depth) => (style.token === "NONE" ? depth + 1 : null))
    .filter((n): n is number => n !== null);
  if (!unlabeled.length) return base;
  return `${base}, with no label on level ${unlabeled.join(" and ")} folders`;
};

/** How a new case numbers when the firm saved no style: "Numbers (1., 1.1.)". */
export const DEFAULT_LABEL_STYLE = labelStyleOf(DEFAULT_EXHIBIT_SCHEME) ?? "";

const citationStyleOf = (
  scheme: Record<string, unknown> | null
): CitationStyle | null =>
  scheme && isRecord(scheme.citation_style)
    ? (scheme.citation_style as CitationStyle)
    : null;

/** The citation styles the backend accepts, as a firm would pick them. */
export const CITATION_PRESETS: readonly {
  id: string;
  prefix: string;
  print_page: boolean;
  pincite: string;
}[] = [
  { id: "exhibit", prefix: "Exhibit", print_page: false, pincite: " at " },
  { id: "ex", prefix: "Ex.", print_page: false, pincite: " at " },
  { id: "exhibit-at", prefix: "Exhibit", print_page: true, pincite: " at " },
  { id: "ex-at", prefix: "Ex.", print_page: true, pincite: " at " },
  { id: "ex-page", prefix: "Ex.", print_page: true, pincite: ", p. " }
];

export const matchCitationPreset = (
  style: CitationStyle | null
): (typeof CITATION_PRESETS)[number] | null => {
  if (!style) return null;
  const prefix = style.prefix?.trim() || "Exhibit";
  const printPage = Boolean(style.print_page);
  const pincite = style.pincite ?? " at ";
  return (
    CITATION_PRESETS.find(
      (p) =>
        p.prefix === prefix &&
        p.print_page === printPage &&
        (!printPage || p.pincite === pincite)
    ) ?? null
  );
};

/** The firm's stored scheme as a case scheme, or null when it has none. */
export const firmSchemeOf = (
  raw: Record<string, unknown> | null | undefined
): ExhibitScheme | null => {
  if (!raw || !isRecord(raw.default_style)) return null;
  return {
    default_style: raw.default_style as unknown as ExhibitStyle,
    levels: Array.isArray(raw.levels) ? (raw.levels as ExhibitStyle[]) : [],
    start_at: typeof raw.start_at === "number" ? raw.start_at : 1
  };
};

/** What a preset applies to: the firm's scheme, or a fresh one from 1. */
const presetBase = (
  scheme: ExhibitScheme | null,
  preset: ExhibitStylePreset
): ExhibitScheme =>
  scheme ?? {
    default_style: preset.levels[0],
    levels: preset.levels,
    start_at: 1
  };

export type SectionProps = {
  lawFirmId: string;
  firm: LawFirm;
  /** Owner / admin (or platform superadmin). */
  canEdit: boolean;
  onSaved: (firm: LawFirm) => void;
};

/** Save fields on the firm record, with a toast; resolves whether it saved. */
export const useFirmSave = ({
  lawFirmId,
  onSaved
}: Pick<SectionProps, "lawFirmId" | "onSaved">) => {
  const [saving, setSaving] = useState(false);
  const save = async (
    payload: Parameters<typeof LawFirmsAPI.update>[1],
    done: string
  ): Promise<boolean> => {
    setSaving(true);
    try {
      const updated = await LawFirmsAPI.update(lawFirmId, payload);
      onSaved(updated);
      announceFirmUpdated(lawFirmId);
      toast.success(done);
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not save the setting."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };
  return { saving, save };
};

const STYLE_PLACEHOLDER = [
  "Passports and IDs: {doc_type} of {subject}",
  "Recommendation letters: {author}, {date}, {description}",
  "Everything else: short and descriptive"
].join("\n");

/**
 * The style is saved as the firm's `title_instructions`: the firm's
 * `default_title_template` takes one line of known fields only. With no
 * instructions saved yet the box starts from the template, which stays as
 * it is.
 */
export const ExhibitTitlesGroup: FC<SectionProps> = ({
  lawFirmId,
  firm,
  canEdit,
  onSaved
}) => {
  const template: TitleTemplate | null = firm.default_title_template ?? null;
  const saved = casePrepValue(firm, "titleInstructions")?.trim() || null;
  const base = saved ?? styleFromTemplate(template);
  const supported = hasCasePrepField(firm, "titleInstructions");
  const [draft, setDraft] = useState(base);
  const [synced, setSynced] = useState(base);
  if (synced !== base) {
    setSynced(base);
    setDraft(base);
  }
  const { saving, save } = useFirmSave({ lawFirmId, onSaved });

  const text = draft.trim();
  const tooLong = text.length > CASE_PREP_TEXT_MAX;
  const changed = text !== (saved ?? "");
  const disabled = !canEdit || saving;

  const store = (value: string | null, done: string) =>
    save(casePrepPatch("titleInstructions", value), done);

  return (
    <CasePrepRow
      title="Title Style"
      testId="firm-house-style"
      description="Describe the style in your own words, or give a format per document type; left empty, CaseBlink's default format for each document type applies. Retitle replaces every file's title, including ones renamed by hand; titles marked Keep stay, and Undo reverts it."
      control={
        saved !== null && canEdit ? (
          <Button
            variant="ghost"
            size="xs"
            className="text-fg-3"
            disabled={disabled || !supported}
            onClick={() => void store(null, "Title instructions cleared")}
            data-testid="house-style-reset-template"
          >
            Clear Title Instructions
          </Button>
        ) : undefined
      }
    >
      <Textarea
        value={draft}
        aria-label="Title Style"
        aria-invalid={tooLong || undefined}
        placeholder={STYLE_PLACEHOLDER}
        readOnly={!canEdit}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        data-testid="house-style-template"
        className={cn(
          "min-h-36 max-h-96",
          !canEdit && "cursor-default bg-fill-1"
        )}
      />
      {canEdit ? (
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {tooLong ? (
            <span className="text-sm text-danger">
              {text.length.toLocaleString()} /{" "}
              {CASE_PREP_TEXT_MAX.toLocaleString()}
            </span>
          ) : null}
          {!supported ? (
            <NeedsBackendNotice testId="house-style-needs-backend" />
          ) : null}
          {draft !== base ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={() => setDraft(base)}
              data-testid="house-style-discard"
            >
              Discard
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            disabled={disabled || !supported || !changed || tooLong}
            onClick={() =>
              void store(
                text || null,
                text ? "Title instructions saved" : "Title instructions cleared"
              )
            }
            data-testid="house-style-save"
          >
            Save
          </Button>
        </div>
      ) : null}
      {saved === null && template ? (
        <p className="text-sm text-fg-3" data-testid="house-style-default">
          Your current title template is filled in; saving keeps it as the
          firm&apos;s title instructions.
        </p>
      ) : null}
    </CasePrepRow>
  );
};

export const ExhibitNumberingGroup: FC<SectionProps> = ({
  lawFirmId,
  firm,
  canEdit,
  onSaved
}) => {
  const scheme = firm.default_exhibit_scheme ?? null;
  const proposal = firm.citation_style_proposal ?? null;
  const { saving, save } = useFirmSave({ lawFirmId, onSaved });
  const disabled = !canEdit || saving;
  const firstLabel = firstLabelOf(scheme);
  const citationStyle = citationStyleOf(scheme);
  const caseScheme = firmSchemeOf(scheme);
  const preset = caseScheme ? matchPreset(caseScheme) : null;
  const citationPreset = matchCitationPreset(citationStyle);
  /** What a citation save spreads: the stored scheme, else CaseBlink's labels. */
  const citationBase = scheme ?? { ...DEFAULT_EXHIBIT_SCHEME };

  const [customOpen, setCustomOpen] = useState(false);

  const saveCustom = (next: ExhibitScheme) =>
    void save(
      { default_exhibit_scheme: { ...(scheme ?? {}), ...next } },
      `New cases use ${labelStyleOf(next) ?? "custom labels"}`
    );

  const savePreset = (id: string) => {
    if (id === CUSTOM_STYLE_VALUE) {
      setCustomOpen(true);
      return;
    }
    const picked = EXHIBIT_STYLE_PRESETS.find((p) => p.id === id);
    if (!picked) return;
    void save(
      {
        default_exhibit_scheme: {
          ...(scheme ?? {}),
          ...applyPreset(presetBase(caseScheme, picked), picked)
        }
      },
      `New cases use ${picked.label}`
    );
  };

  const saveCitation = (id: string) => {
    const picked = CITATION_PRESETS.find((p) => p.id === id);
    if (!picked) return;
    const style: CitationStyle = {
      ...(citationStyle ?? {}),
      prefix: picked.prefix,
      print_page: picked.print_page,
      pincite: picked.pincite,
      separator: citationStyle?.separator ?? "; ",
      source: "manual"
    };
    void save(
      { default_exhibit_scheme: { ...citationBase, citation_style: style } },
      `Citations now read ${citationExample(style)}`
    );
  };

  return (
    <div
      className="flex w-full flex-col divide-y divide-line-1"
      data-testid="house-style-numbering"
    >
      <CasePrepRow
        title="Label Style for New Cases"
        description={
          <>
            <span data-testid="house-style-scheme">
              {firstLabel
                ? `New cases label exhibits starting at "${firstLabel}".`
                : `New cases use CaseBlink's default, ${DEFAULT_LABEL_STYLE}.`}
            </span>{" "}
            Existing cases keep their labels.
          </>
        }
        control={
          <>
            <Select
              value={preset?.id ?? ""}
              onValueChange={savePreset}
              disabled={disabled}
            >
              <SelectTrigger
                aria-label="Label Style for New Cases"
                data-testid="house-style-label-style"
                className="w-72"
              >
                <SelectValue
                  placeholder={
                    scheme
                      ? "Custom Style"
                      : `CaseBlink Default: ${DEFAULT_LABEL_STYLE}`
                  }
                />
              </SelectTrigger>
              <SelectContent position="popper" align="end">
                <ExhibitStyleSelectItems
                  samples={(p) =>
                    presetSamples(presetBase(caseScheme, p), p, 2).join("  ")
                  }
                  testIdPrefix="house-style-label"
                />
              </SelectContent>
            </Select>
            <ExhibitSchemeDialog
              open={customOpen}
              onOpenChange={setCustomOpen}
              scheme={caseScheme ?? DEFAULT_EXHIBIT_SCHEME}
              scope="firm"
              readOnly={!canEdit}
              onSave={saveCustom}
            />
          </>
        }
        actions={
          !canEdit ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setCustomOpen(true)}
              data-testid="house-style-view-levels"
            >
              View Levels
            </Button>
          ) : scheme ? (
            <Button
              variant="ghost"
              size="xs"
              className="text-fg-3"
              disabled={disabled}
              onClick={() =>
                void save(
                  { default_exhibit_scheme: null },
                  citationStyle
                    ? "Labels and citations use CaseBlink's defaults"
                    : "New cases use CaseBlink's labels"
                )
              }
              data-testid="house-style-reset-scheme"
            >
              {citationStyle ? "Reset Labels and Citations" : "Reset"}
            </Button>
          ) : undefined
        }
      />
      <CasePrepRow
        title="Citations in Letters"
        testId="house-style-citations"
        description={
          <span data-testid="house-style-citation-example">
            Every case without its own style cites exhibits as{" "}
            {citationExample(citationStyle)}
            {citationStyle ? "." : ", CaseBlink's default."}
            {!scheme && canEdit
              ? " Picking one also saves CaseBlink's labels for new cases."
              : ""}
          </span>
        }
        control={
          <Select
            value={citationPreset?.id ?? ""}
            onValueChange={saveCitation}
            disabled={disabled}
          >
            <SelectTrigger
              aria-label="Citations in Letters"
              data-testid="house-style-citation-style"
              className="w-72"
            >
              <SelectValue
                placeholder={
                  citationStyle
                    ? `Custom ${citationExample(citationStyle)}`
                    : `CaseBlink Default: ${DEFAULT_CITATION}`
                }
              />
            </SelectTrigger>
            <SelectContent position="popper" align="end">
              {CITATION_PRESETS.map((p) => (
                <SelectItem
                  key={p.id}
                  value={p.id}
                  data-testid={`house-style-citation-${p.id}`}
                >
                  {citationExample(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        actions={
          citationStyle && canEdit ? (
            <Button
              variant="ghost"
              size="xs"
              className="text-fg-3"
              disabled={disabled}
              onClick={() =>
                void save(
                  {
                    default_exhibit_scheme: {
                      ...citationBase,
                      citation_style: null
                    }
                  },
                  "Citations use CaseBlink's style"
                )
              }
              data-testid="house-style-reset-citations"
            >
              Reset
            </Button>
          ) : undefined
        }
      >
        {proposal ? (
          <div
            className="flex w-full items-center justify-between gap-4 rounded-md bg-fill-1 px-3 py-2"
            data-testid="house-style-citation-proposal"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-base text-fg">
                {proposal.source_name
                  ? `"${proposal.source_name}" cites as ${proposal.example}`
                  : `Your playbook cites as ${proposal.example}`}
              </span>
              {proposal.rule ? (
                <span className="truncate text-sm text-fg-3">
                  {proposal.rule}
                </span>
              ) : null}
            </div>
            {canEdit ? (
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() =>
                    void save(
                      { citation_style_proposal: null },
                      "Suggestion dismissed"
                    )
                  }
                  data-testid="house-style-dismiss-citations"
                >
                  Dismiss
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={disabled}
                  onClick={() =>
                    void save(
                      {
                        default_exhibit_scheme: {
                          ...citationBase,
                          citation_style: proposal.style
                        },
                        citation_style_proposal: null
                      },
                      `Citations now read ${proposal.example}`
                    )
                  }
                  data-testid="house-style-use-citations"
                >
                  Use This Style
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </CasePrepRow>
    </div>
  );
};
